import type {
  Annotation,
  Attachment,
  CodeableConcept,
  Coding,
  HumanName,
  Identifier,
  Period,
  Quantity,
  Range,
  Ratio,
  Reference,
} from '@medplum/fhirtypes';

import { type PrimitiveExtension, getDataAbsentReason } from './underscore';

/**
 * Hand-rolled `value[x]` extraction from instance keys. Suffix casing gives
 * the FHIR type name (primitives lowercase-first, complex types as-is), so no
 * StructureDefinition is needed for choice dispatch. Stopgap: intended to be
 * replaced by a schema-driven walker.
 */

const QUANTITY_SUFFIXES = [
  'Quantity',
  'Age',
  'Duration',
  'Count',
  'Distance',
] as const;
const STRING_SUFFIXES = [
  'String',
  'Code',
  'Markdown',
  'Id',
  'Uri',
  'Url',
  'Canonical',
  'Oid',
  'Uuid',
  'Base64Binary',
  'Time',
] as const;
const DATETIME_SUFFIXES = ['DateTime', 'Instant'] as const;
const NUMBER_SUFFIXES = [
  'Integer',
  'PositiveInt',
  'UnsignedInt',
  'Decimal',
] as const;

type ChoiceValueBase = { key: string };

export type ChoiceValue = ChoiceValueBase &
  (
    | { type: 'quantity'; value: Quantity }
    | { type: 'codeableConcept'; value: CodeableConcept }
    | { type: 'coding'; value: Coding }
    | { type: 'period'; value: Period }
    | { type: 'range'; value: Range }
    | { type: 'ratio'; value: Ratio }
    | { type: 'humanName'; value: HumanName }
    | { type: 'identifier'; value: Identifier }
    | { type: 'annotation'; value: Annotation }
    | { type: 'attachment'; value: Attachment }
    | { type: 'reference'; value: Reference }
    | { type: 'dateTime'; value: string }
    | { type: 'date'; value: string }
    | { type: 'string'; value: string }
    | { type: 'number'; value: number }
    | { type: 'boolean'; value: boolean }
    | { type: 'other'; fhirType: string; value: unknown }
    /** Primitive absent, but its `_<element>` sibling carries a data-absent-reason. `base` is the choice prefix (e.g. `value`); `key` the concrete element (e.g. `valueString`). */
    | { type: 'absent'; base: string; dataAbsentReason: string }
  );

function toChoiceValue(
  suffix: string,
  value: unknown,
  key: string,
): ChoiceValue {
  // Dynamic key inspection is the erasure boundary: each arm's value shape is
  // guaranteed by the suffix that selected it, which the compiler can't see.
  if ((QUANTITY_SUFFIXES as readonly string[]).includes(suffix)) {
    return { type: 'quantity', value: value as Quantity, key };
  }
  if ((STRING_SUFFIXES as readonly string[]).includes(suffix)) {
    return { type: 'string', value: value as string, key };
  }
  if ((DATETIME_SUFFIXES as readonly string[]).includes(suffix)) {
    return { type: 'dateTime', value: value as string, key };
  }
  if ((NUMBER_SUFFIXES as readonly string[]).includes(suffix)) {
    return { type: 'number', value: value as number, key };
  }
  switch (suffix) {
    case 'CodeableConcept':
      return { type: 'codeableConcept', value: value as CodeableConcept, key };
    case 'Coding':
      return { type: 'coding', value: value as Coding, key };
    case 'Period':
      return { type: 'period', value: value as Period, key };
    case 'Range':
      return { type: 'range', value: value as Range, key };
    case 'Ratio':
      return { type: 'ratio', value: value as Ratio, key };
    case 'HumanName':
      return { type: 'humanName', value: value as HumanName, key };
    case 'Identifier':
      return { type: 'identifier', value: value as Identifier, key };
    case 'Annotation':
      return { type: 'annotation', value: value as Annotation, key };
    case 'Attachment':
      return { type: 'attachment', value: value as Attachment, key };
    case 'Reference':
      return { type: 'reference', value: value as Reference, key };
    case 'Date':
      return { type: 'date', value: value as string, key };
    case 'Boolean':
      return { type: 'boolean', value: value as boolean, key };
    default:
      return { type: 'other', fhirType: suffix, value, key };
  }
}

export function getChoiceValue(
  obj: object,
  prefix: string,
): ChoiceValue | undefined {
  for (const [key, value] of Object.entries(obj)) {
    // null = GraphQL "selected but absent" — same as the key not existing.
    if (
      !key.startsWith(prefix) ||
      key.length === prefix.length ||
      value === undefined ||
      value === null
    ) {
      continue;
    }
    const suffix = key.slice(prefix.length);
    if (!/^[A-Z]/.test(suffix)) continue;
    return toChoiceValue(suffix, value, key);
  }

  // No value present: the primitive may still carry a data-absent-reason on
  // its `_<element>` underscore sibling — surface it as an absent choice.
  for (const [key, value] of Object.entries(obj)) {
    if (
      !key.startsWith(`_${prefix}`) ||
      value === null ||
      typeof value !== 'object'
    ) {
      continue;
    }
    const element = key.slice(1);
    const suffix = element.slice(prefix.length);
    if (element.length === prefix.length || !/^[A-Z]/.test(suffix)) continue;
    const dataAbsentReason = getDataAbsentReason(value as PrimitiveExtension);
    if (dataAbsentReason) {
      return { type: 'absent', base: prefix, dataAbsentReason, key: element };
    }
  }
  return undefined;
}
